// /srv/docker/develop/strike.hu_51200-51220/next/components/pages/HomePage.jsx

"use client";

import {
    useEffect,
    useState,
} from "react";
import Link from "next/link";
import Image from "next/image";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useLanguage } from "@/components/LanguageContext";
import {
    getLocalizedPath,
} from "@/lib/localizedRoutes";

const translations = {
    hu: {
        heroDescription: (
            <>
                <strong className="block text-[20px] font-black leading-[1.05] text-white md:text-[26px]">
                    8 pálya. 48 dobás.
                    <br />
                    egy teljesen új élmény.
                </strong>

                <span className="mt-3 block">
                    a strike Budapest új interaktív ügyességi játékcentruma,
                    ahol 8 különleges pályán összesen 48 dobás vár rád.
                </span>

                <span className="mt-2 block">
                    nem kell hozzá sportolónak lenned.
                    csak célozz, dobj és gyűjtsd a pontokat!
                </span>

                <span className="mt-2 block">
                    gyere barátokkal, családdal vagy kollégákkal,
                    és derüljön ki, ki talál célba a legtöbbször.
                </span>

                <strong className="mt-3 block font-black text-white">
                    strike. találd el.
                </strong>
            </>
        ),

        bookingButton:
            "foglalj időpontot",

        heroLogoAlt:
            "strike urban playground",

        previousSlide:
            "Előző slide",

        nextSlide:
            "Következő slide",

        slide:
            "slide",

        introductionTitle:
            "strike – Budapest új beltéri élményprogramja",

        introductionFirst: (
            <>
                a strike egy új interaktív ügyességi program Budapesten,{" "}
                <br className="md:hidden" />
                ahol 8 különleges pályán összesen 48 dobás vár rád.{" "}
                <br className="md:hidden" />
                különleges mágneses nyilakkal teheted próbára{" "}
                <br className="md:hidden" />
                a pontosságodat és az ügyességedet.
            </>
        ),

        introductionSecond: (
            <>
                minden pálya más kihívást tartogat,{" "}
                <br className="md:hidden" />
                így minden dobás számít. gyűjts pontokat,{" "}
                <br className="md:hidden" />
                versenyezz a társasággal, és ha eléred az 1500 pontot,{" "}
                <br className="md:hidden" />
                nyisd meg a titkos bónuszpályát: feed the beast!
            </>
        ),

        courses:
            "pálya",

        throws:
            "dobás",

        stepByStep:
            "lépésről lépésre",

        stepOne:
            "érkezz meg, nyisd ki az ajtót, és lépj be a strike világába.",

        stepTwo:
            "haladj végig 8 különleges pályán, pályánként 6 dobással.",

        stepThree:
            "célozz, dobj, találd el a célokat, és gyűjts minél több pontot.",

        stepFour:
            "érd el az 1500 pontot, és nyisd meg a titkos bónuszpályát: feed the beast!",

        stepFive:
            "még több élményre vágysz? próbáld ki különtermeinket is!",

        firstStepImageAlt:
            "nyisd ki az ajtót, és lépj be a strike játékterébe",

        secondStepImageAlt:
            "nyolc különleges ügyességi pálya a strike-ban",

        thirdStepImageAlt:
            "mágneses nyíldobás a strike célfelületére",

        feedTheBeastImageAlt:
            "feed the beast titkos bónuszpálya",

        privateRoomsImageAlt:
            "a strike különtermei és private room élményei",

        coursesTitle:
            "nyolc pálya. nyolc különböző kihívás.",

        coursesParagraphs: [
            "a strike-ban 8 különleges pályán haladsz végig.",
            "minden pályán 6 dobásod van, így egy teljes játék során összesen 48 alkalommal teheted próbára a pontosságodat és az ügyességedet.",
            <>
                a célfelületek különböző pontértékeket érnek, és{" "}
                <strong className="font-black text-black">
                    minden célfelületre csak egyszer szerezhetsz pontot.
                </strong>
            </>,
        ],

        coursesClosing:
            "vajon ki lesz a társaság legjobb dobója?",

        occasionsTitle:
            "egy program, rengeteg alkalomra",

        occasionsParagraphs: [
            <>
                a strike akkor is jó választás, ha{" "}
                <strong className="font-black text-black">
                    szülinapi programot
                </strong>{" "}
                keresel Budapesten, és akkor is, ha barátokkal vagy családdal szeretnétek valami igazán közöset csinálni.
            </>,
            <>
                céges társasággal érkeztek? a strike egy{" "}
                <strong className="font-black text-black">
                    interaktív csapatépítő és céges program
                </strong>
                , ahol nem csak egymás mellett vagytok, hanem együtt játszotok, versenyeztek és szurkoltok.
            </>,
            <>
                és az időjárás sem számít: esőben, hidegben vagy a legnagyobb nyári melegben is{" "}
                <strong className="font-black text-black">
                    beltéri program Budapesten
                </strong>
                , amit egész évben élvezhettek.
            </>,
        ],

        occasionsClosing:
            "nézz szét oldalainkon, és válaszd ki, hogyan jönnétek.",

        occasionsLinks: {
            indoor:
                "beltéri program",
            birthday:
                "szülinap",
            corporate:
                "csapatépítés",
            friends:
                "barátokkal",
        },

        bottomBookingTitle:
            "készen állsz?",

        bottomBookingDescription: (
            <>
                8 pálya.
                <br />
                48 dobás.
                <br />
                egy új budapesti élmény.
                <br />
                <br />
                barátokkal. családdal. kollégákkal.
                <br />
                <strong className="font-black text-black">
                    strike. találd el.
                </strong>
            </>
        ),

        bottomBookingButton:
            "foglalj időpontot",
    },

    en: {
        heroDescription: (
            <>
                <strong className="block text-[20px] font-black leading-[1.05] text-white md:text-[26px]">
                    8 courses. 48 throws.
                    <br />
                    one completely new experience.
                </strong>

                <span className="mt-3 block">
                    strike is Budapest&apos;s new interactive skill-based game centre,
                    with 8 unique courses and a total of 48 throws.
                </span>

                <span className="mt-2 block">
                    you don&apos;t need to be an athlete.
                    just aim, throw and collect points!
                </span>

                <span className="mt-2 block">
                    come with friends, family or colleagues
                    and find out who can hit the most targets.
                </span>

                <strong className="mt-3 block font-black text-white">
                    strike. hit it.
                </strong>
            </>
        ),

        bookingButton:
            "book now",

        heroLogoAlt:
            "strike urban playground",

        previousSlide:
            "Previous slide",

        nextSlide:
            "Next slide",

        slide:
            "slide",

        introductionTitle:
            "strike – Budapest's new indoor experience",

        introductionFirst: (
            <>
                strike is a new interactive skill-based experience in Budapest,{" "}
                <br className="md:hidden" />
                with 8 unique courses and a total of 48 throws.{" "}
                <br className="md:hidden" />
                use our special magnetic arrows to put your{" "}
                <br className="md:hidden" />
                accuracy and skills to the test.
            </>
        ),

        introductionSecond: (
            <>
                every course offers a different challenge,{" "}
                <br className="md:hidden" />
                so every throw counts. collect points, compete{" "}
                <br className="md:hidden" />
                with your group and, if you reach 1500 points,{" "}
                <br className="md:hidden" />
                unlock the secret bonus game: feed the beast!
            </>
        ),

        courses:
            "courses",

        throws:
            "throws",

        stepByStep:
            "step by step",

        stepOne:
            "arrive, open the door and step into the world of strike.",

        stepTwo:
            "make your way through 8 unique courses, with 6 throws on each one.",

        stepThree:
            "aim, throw, hit the targets and collect as many points as possible.",

        stepFour:
            "reach 1500 points and unlock the secret bonus course: feed the beast!",

        stepFive:
            "want even more? discover our private rooms too!",

        firstStepImageAlt:
            "open the door and enter the strike playing area",

        secondStepImageAlt:
            "eight unique skill-based courses at strike",

        thirdStepImageAlt:
            "throwing a magnetic arrow at a strike target",

        feedTheBeastImageAlt:
            "feed the beast secret bonus course",

        privateRoomsImageAlt:
            "private rooms and additional experiences at strike",

        coursesTitle:
            "eight courses. eight different challenges.",

        coursesParagraphs: [
            "at strike, you make your way through 8 unique courses.",
            "you have 6 throws on each course, giving you a total of 48 chances to test your accuracy and skill.",
            <>
                the targets are worth different numbers of points, and{" "}
                <strong className="font-black text-black">
                    each target can only score once.
                </strong>
            </>,
        ],

        coursesClosing:
            "who will be the best thrower in your group?",

        occasionsTitle:
            "one experience, plenty of occasions",

        occasionsParagraphs: [
            <>
                strike is a great choice if you are looking for a{" "}
                <strong className="font-black text-black">
                    birthday activity in Budapest
                </strong>
                , or simply want to do something different with friends or family.
            </>,
            <>
                coming with colleagues? strike is an{" "}
                <strong className="font-black text-black">
                    interactive team-building and corporate activity
                </strong>{" "}
                where you play, compete and cheer together.
            </>,
            <>
                and the weather does not matter either. rain, cold or summer heat, strike is an{" "}
                <strong className="font-black text-black">
                    indoor activity in Budapest
                </strong>{" "}
                you can enjoy all year round.
            </>,
        ],

        occasionsClosing:
            "explore our pages and find the right strike experience for your group.",

        occasionsLinks: {
            indoor:
                "indoor activity",
            birthday:
                "birthday",
            corporate:
                "team building",
            friends:
                "with friends",
        },

        bottomBookingTitle:
            "ready to play?",

        bottomBookingDescription: (
            <>
                8 courses.
                <br />
                48 throws.
                <br />
                a new Budapest experience.
                <br />
                <br />
                with friends. family. colleagues.
                <br />
                <strong className="font-black text-black">
                    strike. hit it.
                </strong>
            </>
        ),

        bottomBookingButton:
            "book now",
    },
};

const heroSlides = [
    /*
        {
            id: 1,
            backgroundType: "image",
            backgroundColor: "#000000",
            //backgroundSrc: "/v4/hero-background.gif",
            //logo: "/v4/strike-urban-playground-logo-white.png",
            //logoAlt: "Strike Urban Playground",
            logo: "/v4/hero-background.mp4",
            buttonHref: "/booking",
            overlayClass: "bg-black/50",
        },
    
        /*
                    {
                id: 2,
                backgroundType: "color",
                backgroundColor: "#000000",
                backgroundSrc: null,
                logo: "/v4/hero-background.gif",
                logoAlt: "Strike Urban Playground animáció",
                description: (
                    <>
                        <span className="block text-[28px] font-black lowercase leading-none text-white md:text-[42px]">
                            nyitás hamarosan
                        </span>
        
                        <span className="mt-5 block text-[15px] leading-[1.3] text-white/75 md:text-[20px] md:leading-[1.4]">
                            már nem kell sokat várnod,
                            <br />
                            hamarosan találkozunk.
                        </span>
                    </>
                ),
                buttonText: null,
                buttonHref: null,
                overlayClass: "",
            },*/



    {
        id: 3,
        backgroundType: "video",
        backgroundColor: "#000000",
        backgroundSrcMobile: "/slider/strike-slider-mobile.mp4",
        backgroundSrcDesktop: "/slider/strike-slider-desktop.mp4",
        logo: "/v4/strike-urban-playground-logo-white.png",
        logoAlt: "Strike Urban Playground",
        description: (
            <>
                próbáld ki Budapest
                <br />
                új interaktív
                <br />
                játékcentrumát.
            </>
        ),
        buttonText: "időpontfoglalás",
        buttonHref: "/booking",
        overlayClass: "bg-black/25",
    },

];

function HeroSlider({
    text,
}) {
    const [activeIndex, setActiveIndex] = useState(0);

    useEffect(() => {
        if (heroSlides.length <= 1) {
            return undefined;
        }

        const interval = window.setInterval(() => {
            setActiveIndex((currentIndex) =>
                currentIndex === heroSlides.length - 1
                    ? 0
                    : currentIndex + 1,
            );
        }, 7000);

        return () => {
            window.clearInterval(interval);
        };
    }, []);

    const showPreviousSlide = () => {
        setActiveIndex((currentIndex) =>
            currentIndex === 0
                ? heroSlides.length - 1
                : currentIndex - 1,
        );
    };

    const showNextSlide = () => {
        setActiveIndex((currentIndex) =>
            currentIndex === heroSlides.length - 1
                ? 0
                : currentIndex + 1,
        );
    };

    return (
        <section className="relative min-h-[590px] overflow-hidden bg-black text-white md:min-h-[680px]">
            {/* SLIDE-OK */}
            <div className="absolute inset-0">
                {heroSlides.map((slide, index) => (
                    <div
                        key={slide.id}
                        className="absolute inset-0 overflow-hidden transition-transform duration-700 ease-in-out"
                        style={{
                            backgroundColor:
                                slide.backgroundColor || "#000000",
                            transform: `translateX(${(index - activeIndex) * 100
                                }%)`,
                        }}
                    >
                        {slide.backgroundType === "image" &&
                            slide.backgroundSrc && (
                                <img
                                    src={slide.backgroundSrc}
                                    alt=""
                                    aria-hidden="true"
                                    className="absolute inset-0 h-full w-full object-cover"
                                />
                            )}

                        {slide.backgroundType === "video" &&
                            slide.backgroundSrcMobile &&
                            slide.backgroundSrcDesktop && (
                                <video
                                    autoPlay
                                    muted
                                    loop
                                    playsInline
                                    preload="metadata"
                                    aria-hidden="true"
                                    className="absolute inset-0 h-full w-full object-cover"
                                >
                                    <source
                                        src={slide.backgroundSrcMobile}
                                        media="(max-width: 767px)"
                                        type="video/mp4"
                                    />

                                    <source
                                        src={slide.backgroundSrcDesktop}
                                        media="(min-width: 768px)"
                                        type="video/mp4"
                                    />
                                </video>
                            )}

                        {slide.overlayClass && (
                            <>
                                <div
                                    aria-hidden="true"
                                    className={`absolute inset-0 ${slide.overlayClass}`}
                                />

                                {slide.backgroundType === "video" && (
                                    <div
                                        aria-hidden="true"
                                        className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/35 to-black/10"
                                    />
                                )}
                            </>
                        )}

                        <div className="relative z-10 mx-auto flex min-h-[590px] w-full max-w-[430px] flex-col items-center px-6 pb-10 md:min-h-[680px] md:max-w-none md:px-12 md:pb-12">
                            <div className="flex flex-1 flex-col items-center justify-start md:w-full md:max-w-6xl">
                                <div className="flex flex-col items-center text-center">
                                    <video
                                        src={slide.logo}
                                        autoPlay
                                        muted
                                        loop
                                        playsInline
                                        preload="auto"
                                        aria-label={
                                            text.heroLogoAlt
                                        }
                                        width={1000}
                                        height={1000}
                                        className="h-auto w-[205px] object-contain md:w-[300px]"
                                    />

                                    <div className="mt-4 max-w-[320px] rounded-[8px] bg-black/15 px-4 py-3 text-[13px] leading-[1.25] text-white md:max-w-[500px] md:px-5 md:py-4 md:text-[15px] md:leading-[1.35]">
                                        {text.heroDescription}
                                    </div>

                                    {slide.buttonHref && (
                                        <Link
                                            href={
                                                slide.buttonHref
                                            }
                                            onClick={() => {
                                                if (
                                                    typeof window.gtag ===
                                                    "function" &&
                                                    window
                                                        .googleAnalyticsInitialized ===
                                                    true &&
                                                    window
                                                        .googleAnalyticsTrackingEnabled ===
                                                    true
                                                ) {
                                                    window.gtag(
                                                        "event",
                                                        "start_booking",
                                                        {
                                                            source:
                                                                "homepage_hero",

                                                            debug_mode:
                                                                process.env.NODE_ENV !==
                                                                "production",
                                                        }
                                                    );
                                                }

                                                if (
                                                    typeof window.fbq ===
                                                    "function" &&
                                                    window
                                                        .metaPixelTrackingEnabled ===
                                                    true
                                                ) {
                                                    window.fbq(
                                                        "trackCustom",
                                                        "StartBooking",
                                                        {
                                                            source:
                                                                "homepage_hero",
                                                        },
                                                        {
                                                            eventID:
                                                                `start_booking_${Date.now()}`,
                                                        }
                                                    );
                                                }
                                            }}
                                            className="
                                                    mt-5 flex h-[48px]
                                                    w-[270px] cursor-pointer
                                                    select-none items-center
                                                    justify-center overflow-hidden
                                                    rounded-[6px] bg-[#f5a400]
                                                    px-6 text-[18px] font-black
                                                    text-black transition-transform
                                                    duration-200 ease-out
                                                    hover:scale-[1.02]
                                                    active:translate-y-[2px]
                                                    active:scale-95
                                                    [-webkit-touch-callout:none]
                                                    md:h-[56px] md:w-[320px]
                                                    md:text-[21px]
                                                    md:hover:scale-105
                                                    button-pulse
                                                "
                                        >
                                            {text.bookingButton}
                                        </Link>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* NAVIGÁCIÓ */}
            {heroSlides.length > 1 && (
                <>
                    <button
                        type="button"
                        onClick={showPreviousSlide}
                        aria-label={
                            text.previousSlide
                        }
                        className="absolute left-3 top-1/2 z-20 flex h-10 w-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border-2 border-white/60 bg-black/30 text-white backdrop-blur-sm transition hover:border-white hover:bg-black/60 md:left-8 md:h-12 md:w-12"
                    >
                        <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            className="h-5 w-5 md:h-6 md:w-6"
                        >
                            <path
                                d="M15 18L9 12L15 6"
                                stroke="currentColor"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </button>

                    <button
                        type="button"
                        onClick={showNextSlide}
                        aria-label={
                            text.nextSlide
                        }
                        className="absolute right-3 top-1/2 z-20 flex h-10 w-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border-2 border-white/60 bg-black/30 text-white backdrop-blur-sm transition hover:border-white hover:bg-black/60 md:right-8 md:h-12 md:w-12"
                    >
                        <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            className="h-5 w-5 md:h-6 md:w-6"
                        >
                            <path
                                d="M9 6L15 12L9 18"
                                stroke="currentColor"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </button>

                    <div className="absolute inset-x-0 bottom-7 z-20 flex items-center justify-center gap-2 md:bottom-9">
                        {heroSlides.map((slide, index) => (
                            <button
                                key={slide.id}
                                type="button"
                                onClick={() =>
                                    setActiveIndex(index)
                                }
                                aria-label={`${index + 1}. ${text.slide}`}
                                aria-current={
                                    activeIndex === index
                                        ? "true"
                                        : undefined
                                }
                                className={`h-2.5 cursor-pointer rounded-full transition-all duration-300 ${activeIndex === index
                                    ? "w-8 bg-[#f5a400]"
                                    : "w-2.5 bg-white/50 hover:bg-white"
                                    }`}
                            />
                        ))}
                    </div>
                </>
            )}
        </section>
    );
}

export default function Index() {
    const {
        language,
    } =
        useLanguage();

    const text =
        translations[language] ||
        translations.hu;

    const firstStepImage =
        language === "en"
            ? "/v4/nyisd_az_ajtot_strike-en.webp"
            : "/v4/nyisd_az_ajtot_strike.webp";

    const secondStepImage =
        language === "en"
            ? "/v4/nyolc_palya_strike-en.webp"
            : "/v4/nyolc_palya_strike.webp";

    const thirdStepImage =
        language === "en"
            ? "/v4/csak_te_csak_a_cel-en.webp"
            : "/v4/csak_te_csak_a_cel.webp";

    const feedTheBeastImage =
        language === "en"
            ? "/v4/feed_the_beast_strike-en.webp"
            : "/v4/feed_the_beast_strike.webp";

    return (
        <main className="min-h-screen overflow-x-hidden bg-black font-sans">
            <Header showBookingButton={false} />

            {/* HERO SLIDER */}
            <HeroSlider
                text={
                    text
                }
            />

            {/* EZ A STRIKE */}
            <section className="bg-[#e6e6e6] text-black">
                <div className="mx-auto flex min-h-[475px] w-full max-w-[430px] flex-col items-center px-6 pb-12 pt-16 text-center md:max-w-6xl md:min-h-[580px] md:justify-center md:px-12 md:py-20">
                    <h1 className="text-[39px] font-black lowercase leading-none tracking-[-0.025em] md:text-[62px]">
                        {text.introductionTitle}
                    </h1>

                    <div className="mt-10 max-w-[330px] text-[16px] font-medium leading-[1.22] text-neutral-600 md:max-w-2xl md:text-[17px] md:leading-[1.45]">
                        <p>
                            {text.introductionFirst}
                        </p>

                        <p className="mt-4">
                            {text.introductionSecond}
                        </p>
                    </div>

                    <div className="mt-11 flex items-center justify-center gap-2.5 md:gap-7">
                        <div className="flex h-[67px] w-[67px] flex-col items-center justify-center rounded-full border-[3px] border-neutral-700 bg-white md:h-[90px] md:w-[90px]">
                            <strong className="text-[20px] font-black leading-[0.8] md:text-[28px]">
                                8
                            </strong>
                            <span className="mt-1 text-[11px] font-black leading-none md:text-[14px]">
                                {text.courses}
                            </span>
                        </div>

                        <div className="flex h-[67px] w-[67px] flex-col items-center justify-center rounded-full border-[3px] border-neutral-700 bg-white md:h-[90px] md:w-[90px]">
                            <strong className="text-[20px] font-black leading-[0.8] md:text-[28px]">
                                48
                            </strong>
                            <span className="mt-1 text-[11px] font-black leading-none md:text-[14px]">
                                {text.throws}
                            </span>
                        </div>

                        <div className="flex h-[67px] w-[67px] items-center justify-center overflow-hidden rounded-full border-[3px] border-neutral-700 bg-white md:h-[90px] md:w-[90px]">
                            <Image
                                src="/v4/monster.png"
                                alt="Feed the Beast"
                                width={46}
                                height={60}
                                sizes="(min-width: 768px) 65px, 46px"
                                className="h-[60px] w-[46px] object-contain md:h-[65px] md:w-[65px]"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* 8 PÁLYA – 48 DOBÁS */}
            <SeoContentSection
                title={
                    text.coursesTitle
                }
                paragraphs={
                    text.coursesParagraphs
                }
                closing={
                    text.coursesClosing
                }
                alternate
            />

            {/* BEMUTATÓ VIDEÓ - MOBIL */}
            <section
                id="video-mobile"
                className="block bg-black md:hidden"
            >
                <div className="w-full">
                    <video
                        src="/v4/strike-bemutato.mp4"
                        autoPlay
                        muted
                        loop
                        playsInline
                        controls
                        className="block h-auto w-full"
                    />
                </div>
            </section>


            {/* BEMUTATÓ VIDEÓ - DESKTOP */}
            <section
                id="video-desktop"
                className="hidden bg-black py-20 md:block"
            >
                <div
                    style={{
                        width: "min(100%, 50%)",
                        margin: "0 auto",
                    }}
                >
                    <video
                        src="/v4/2026_07_28_STRIKE_01_KÉPES_9-16_V2.mp4"
                        autoPlay
                        muted
                        loop
                        playsInline
                        controls
                        style={{
                            display: "block",
                            width: "100%",
                            height: "auto",
                        }}
                    />
                </div>
            </section>

            {/* LÉPÉSRŐL LÉPÉSRE ÉS 1. LÉPÉS */}
            <section className="bg-[#e6e6e6] text-black">
                <div className="mx-auto w-full max-w-[430px] px-7 pb-16 pt-14 md:max-w-6xl md:px-12 md:pb-16 md:pt-20">
                    <h2 className="text-center text-[48px] font-black leading-[0.92] tracking-[-0.045em] md:text-[72px]">
                        {text.stepByStep}
                    </h2>

                    <div className="mx-auto mt-16 flex w-fit items-center justify-center md:max-w-xl">
                        <div className="flex h-[70px] w-[70px] shrink-0 items-center justify-center rounded-full border-[3px] border-neutral-700 bg-white text-[42px] font-black leading-none md:h-[88px] md:w-[88px] md:text-[52px]">
                            1
                        </div>

                        <p className="ml-6 text-[16px] font-medium text-neutral-600 md:text-[17px]">
                            {text.stepOne}
                        </p>
                    </div>
                </div>
            </section>

            {/* 1. KÉP */}
            <section className="relative overflow-hidden bg-black">

                {/* BLUR HÁTTÉR */}
                <Image
                    src={
                        firstStepImage
                    }
                    alt=""
                    aria-hidden="true"
                    fill
                    sizes="(min-width: 900px) 900px, 100vw"
                    className="scale-[1.15] object-cover blur-[30px] opacity-70"
                />

                {/* ÉLES KÉP */}
                <div className="relative mx-auto w-full max-w-[900px]">
                    <Image
                        src={
                            firstStepImage
                        }
                        alt={
                            text.firstStepImageAlt
                        }
                        width={1535}
                        height={1024}
                        sizes="(min-width: 900px) 900px, 100vw"
                        className="block h-auto w-full"
                    />
                </div>

            </section>

            {/* 2. LÉPÉS */}
            <section className="bg-[#e6e6e6] text-black">
                <div className="mx-auto flex min-h-[200px] w-full max-w-[430px] items-center justify-center px-7 py-10 md:max-w-4xl md:min-h-[240px] md:px-12">
                    <div className="flex h-[70px] w-[70px] shrink-0 items-center justify-center rounded-full border-[3px] border-neutral-700 bg-white text-[42px] font-black leading-none md:h-[88px] md:w-[88px] md:text-[52px]">
                        2
                    </div>

                    <p className="ml-6 max-w-[240px] text-[16px] font-medium leading-[1.12] text-neutral-500 md:max-w-none md:text-[17px] md:leading-normal">
                        {text.stepTwo}
                    </p>
                </div>
            </section>

            {/* 2. KÉP */}
            <section className="relative overflow-hidden bg-black">

                {/* BLUR HÁTTÉR */}
                <Image
                    src={
                        secondStepImage
                    }
                    alt=""
                    aria-hidden="true"
                    fill
                    sizes="(min-width: 900px) 900px, 100vw"
                    className="scale-[1.15] object-cover blur-[30px] opacity-70"
                />

                {/* ÉLES KÉP */}
                <div className="relative mx-auto w-full max-w-[900px]">
                    <Image
                        src={
                            secondStepImage
                        }
                        alt={
                            text.secondStepImageAlt
                        }
                        width={1536}
                        height={1024}
                        sizes="(min-width: 900px) 900px, 100vw"
                        className="block h-auto w-full"
                    />
                </div>

            </section>

            {/* 3. LÉPÉS */}
            <section className="bg-[#e6e6e6] text-black">
                <div className="mx-auto flex min-h-[200px] w-full max-w-[430px] items-center justify-center px-7 py-10 md:max-w-4xl md:min-h-[240px] md:px-12">
                    <div className="flex h-[70px] w-[70px] shrink-0 items-center justify-center rounded-full border-[3px] border-neutral-700 bg-white text-[42px] font-black leading-none md:h-[88px] md:w-[88px] md:text-[52px]">
                        3
                    </div>

                    <p className="ml-6 max-w-[250px] text-[16px] font-medium leading-[1.12] text-neutral-500 md:max-w-none md:text-[17px] md:leading-normal">
                        {text.stepThree}
                    </p>
                </div>
            </section>

            {/* 3. KÉP */}
            <section className="relative overflow-hidden bg-black">

                {/* BLUR HÁTTÉR */}
                <Image
                    src={
                        thirdStepImage
                    }
                    alt=""
                    aria-hidden="true"
                    fill
                    sizes="(min-width: 900px) 900px, 100vw"
                    className="scale-[1.15] object-cover blur-[30px] opacity-70"
                />

                {/* ÉLES KÉP */}
                <div className="relative mx-auto w-full max-w-[900px]">
                    <Image
                        src={
                            thirdStepImage
                        }
                        alt={
                            text.thirdStepImageAlt
                        }
                        width={1536}
                        height={1024}
                        sizes="(min-width: 900px) 900px, 100vw"
                        className="block h-auto w-full"
                    />
                </div>

            </section>

            {/* 4. LÉPÉS */}
            <section className="bg-[#e6e6e6] text-black">
                <div className="mx-auto flex min-h-[200px] w-full max-w-[430px] items-center justify-center px-7 py-10 md:max-w-4xl md:min-h-[240px] md:px-12">
                    <div className="flex h-[70px] w-[70px] shrink-0 items-center justify-center rounded-full border-[3px] border-neutral-700 bg-white text-[42px] font-black leading-none md:h-[88px] md:w-[88px] md:text-[52px]">
                        4
                    </div>

                    <p className="ml-6 max-w-[250px] text-[16px] font-medium leading-[1.13] text-neutral-500 md:max-w-none md:text-[17px] md:leading-normal">
                        {text.stepFour}
                    </p>
                </div>
            </section>

            {/* FEED THE BEAST KÉP */}
            <section className="relative overflow-hidden bg-black">

                {/* BLUR HÁTTÉR */}
                <Image
                    src={
                        feedTheBeastImage
                    }
                    alt=""
                    aria-hidden="true"
                    fill
                    sizes="(min-width: 900px) 900px, 100vw"
                    className="scale-[1.15] object-cover blur-[30px] opacity-70"
                />

                {/* ÉLES KÉP */}
                <div className="relative mx-auto w-full max-w-[900px]">
                    <Image
                        src={
                            feedTheBeastImage
                        }
                        alt={
                            text.feedTheBeastImageAlt
                        }
                        width={1254}
                        height={1254}
                        sizes="(min-width: 900px) 900px, 100vw"
                        className="block h-auto w-full"
                    />
                </div>

            </section>

            {/* 5. LÉPÉS */}
            <section className="bg-[#e6e6e6] text-black">
                <div className="mx-auto flex min-h-[200px] w-full max-w-[430px] items-center justify-center px-7 py-10 md:min-h-[240px] md:max-w-4xl md:px-12">
                    <div className="flex h-[70px] w-[70px] shrink-0 items-center justify-center rounded-full border-[3px] border-neutral-700 bg-white text-[42px] font-black leading-none md:h-[88px] md:w-[88px] md:text-[52px]">
                        5
                    </div>

                    <p className="ml-6 max-w-[250px] text-[16px] font-medium leading-[1.2] text-neutral-500 md:max-w-none md:text-[17px] md:leading-normal">
                        {text.stepFive}
                    </p>
                </div>
            </section>

            {/* KÜLÖNTERMEK KÉP */}
            <section className="relative overflow-hidden bg-black">

                {/* BLUR HÁTTÉR */}
                <Image
                    src="/v4/probald_ki_a_kulontermeinket_is.webp"
                    alt=""
                    aria-hidden="true"
                    fill
                    sizes="(min-width: 900px) 900px, 100vw"
                    className="scale-[1.15] object-cover blur-[30px] opacity-70"
                />

                {/* ÉLES KÉP */}
                <div className="relative mx-auto w-full max-w-[900px]">
                    <Image
                        src="/v4/probald_ki_a_kulontermeinket_is.webp"
                        alt={
                            text.privateRoomsImageAlt
                        }
                        width={1536}
                        height={1024}
                        sizes="(min-width: 900px) 900px, 100vw"
                        className="block h-auto w-full"
                    />
                </div>

            </section>

            {/* MIRE JÓ A STRIKE? */}
            <section className="bg-[#f2f2f2] text-black">
                <div className="mx-auto flex w-full max-w-[430px] flex-col items-center px-7 py-16 text-center md:max-w-5xl md:px-12 md:py-24">
                    <h2 className="max-w-[350px] text-[31px] font-black lowercase leading-[1.02] tracking-[-0.025em] md:max-w-4xl md:text-[50px]">
                        {text.occasionsTitle}
                    </h2>

                    <div className="mt-8 max-w-[340px] text-[15px] font-medium leading-[1.5] text-neutral-600 md:max-w-2xl md:text-[18px] md:leading-[1.55]">
                        {text.occasionsParagraphs.map(
                            (
                                paragraph,
                                index
                            ) => (
                                <p
                                    key={
                                        index
                                    }
                                    className={
                                        index === 0
                                            ? ""
                                            : "mt-5"
                                    }
                                >
                                    {paragraph}
                                </p>
                            )
                        )}
                    </div>

                    <strong className="mt-8 max-w-[350px] text-[17px] font-black lowercase leading-[1.25] md:max-w-2xl md:text-[21px]">
                        {text.occasionsClosing}
                    </strong>

                    <nav
                        aria-label="strike programok"
                        className="mt-8 flex max-w-[720px] flex-wrap items-center justify-center gap-2.5"
                    >
                        <Link
                            href={getLocalizedPath(
                                "/belteri-program-budapest",
                                language
                            )}
                            className="rounded-full border-2 border-black px-5 py-2.5 text-[14px] font-black lowercase transition-colors hover:bg-black hover:text-white md:text-[16px]"
                        >
                            {text.occasionsLinks.indoor}
                        </Link>

                        <Link
                            href={getLocalizedPath(
                                "/szulinap-budapest",
                                language
                            )}
                            className="rounded-full border-2 border-black px-5 py-2.5 text-[14px] font-black lowercase transition-colors hover:bg-black hover:text-white md:text-[16px]"
                        >
                            {text.occasionsLinks.birthday}
                        </Link>

                        <Link
                            href={getLocalizedPath(
                                "/csapatepites-budapest",
                                language
                            )}
                            className="rounded-full border-2 border-black px-5 py-2.5 text-[14px] font-black lowercase transition-colors hover:bg-black hover:text-white md:text-[16px]"
                        >
                            {text.occasionsLinks.corporate}
                        </Link>

                        <Link
                            href={getLocalizedPath(
                                "/program-baratokkal-budapest",
                                language
                            )}
                            className="rounded-full border-2 border-black px-5 py-2.5 text-[14px] font-black lowercase transition-colors hover:bg-black hover:text-white md:text-[16px]"
                        >
                            {text.occasionsLinks.friends}
                        </Link>
                    </nav>
                </div>
            </section>

            {/* ALSÓ FOGLALÁS */}
            <section className="bg-[#e6e6e6] text-black">
                <div className="mx-auto flex w-full max-w-[430px] flex-col items-center px-7 py-16 text-center md:max-w-5xl md:px-12 md:py-24">
                    <h2 className="text-[30px] font-black lowercase leading-none md:text-[48px]">
                        {text.bottomBookingTitle}
                    </h2>

                    <p className="mt-5 max-w-[300px] text-[15px] leading-[1.35] text-neutral-600 md:max-w-xl md:text-[19px]">
                        {text.bottomBookingDescription}
                    </p>

                    <Link
                        href="/booking"
                        onClick={() => {
                            if (
                                typeof window.gtag ===
                                "function" &&
                                window
                                    .googleAnalyticsInitialized ===
                                true &&
                                window
                                    .googleAnalyticsTrackingEnabled ===
                                true
                            ) {
                                window.gtag(
                                    "event",
                                    "start_booking",
                                    {
                                        source:
                                            "homepage_bottom",

                                        debug_mode:
                                            process.env.NODE_ENV !==
                                            "production",
                                    }
                                );
                            }

                            if (
                                typeof window.fbq ===
                                "function" &&
                                window
                                    .metaPixelTrackingEnabled ===
                                true
                            ) {
                                window.fbq(
                                    "trackCustom",
                                    "StartBooking",
                                    {
                                        source:
                                            "homepage_bottom",
                                    },
                                    {
                                        eventID:
                                            `start_booking_${Date.now()}`,
                                    }
                                );
                            }
                        }}
                        className="
                            mt-9 flex h-[52px]
                            min-w-[240px]
                            cursor-pointer select-none
                            items-center justify-center
                            rounded-[6px]
                            bg-[#f5a400] px-7
                            text-[18px] font-black
                            text-black transition-transform
                            duration-200 ease-out
                            hover:scale-[1.03]
                            active:translate-y-[2px]
                            active:scale-95
                            [-webkit-touch-callout:none]
                            md:mt-11 md:h-[60px]
                            md:min-w-[280px]
                            md:text-[21px]
                            button-pulse
                        "
                    >
                        {text.bottomBookingButton}
                    </Link>
                </div>
            </section>

            {/* FOOTER */}
            <Footer />
        </main>
    );
}

function SeoContentSection({
    title,
    paragraphs,
    closing,
    href,
    buttonText,
    alternate = false,
}) {
    return (
        <section
            className={
                alternate
                    ? "bg-[#f2f2f2] text-black"
                    : "bg-[#e6e6e6] text-black"
            }
        >
            <div className="mx-auto flex w-full max-w-[430px] flex-col items-center px-7 py-14 text-center md:max-w-5xl md:px-12 md:py-20">
                <h2 className="max-w-[350px] text-[29px] font-black lowercase leading-[1.02] tracking-[-0.025em] md:max-w-4xl md:text-[46px]">
                    {title}
                </h2>

                <div className="mt-7 max-w-[330px] text-[15px] font-medium leading-[1.4] text-neutral-600 md:max-w-2xl md:text-[18px] md:leading-[1.5]">
                    {paragraphs.map(
                        (
                            paragraph,
                            index
                        ) => (
                            <p
                                key={
                                    index
                                }
                                className={
                                    index === 0
                                        ? ""
                                        : "mt-4"
                                }
                            >
                                {paragraph}
                            </p>
                        )
                    )}
                </div>

                {closing && (
                    <strong className="mt-7 max-w-[330px] text-[17px] font-black lowercase leading-[1.25] md:max-w-2xl md:text-[21px]">
                        {closing}
                    </strong>
                )}

                {href &&
                    buttonText && (
                        <Link
                            href={
                                href
                            }
                            className="
                                mt-8 flex h-[52px]
                                min-w-[240px]
                                items-center justify-center
                                rounded-[6px]
                                bg-[#f5a400] px-7
                                text-[17px] font-black
                                text-black
                                transition-transform
                                duration-200 ease-out
                                hover:scale-[1.03]
                                active:translate-y-[2px]
                                active:scale-95
                                md:h-[58px]
                                md:min-w-[280px]
                                md:text-[20px]
                            "
                        >
                            {buttonText}
                        </Link>
                    )}
            </div>
        </section>
    );
}